import {
  ROTATION_SHIFT_SNAP_INCREMENT,
  normalizeRotationDegrees,
  snapRotationToIncrement,
} from '@/lib/transforms/geometry'
import type { ResolvedRotationSnap } from '../types/resolved-rotation-snap'

export function getSingleShapeRotationGuideCenter(shape: {
  x: number
  y: number
  width: number
  height: number
}) {
  return {
    centerX: shape.x + shape.width / 2,
    centerY: shape.y + shape.height / 2,
  }
}

/**
 * Integer degrees that match rotation snap guide axes (same targets as `snapRotationAngle`, excluding 360 which aliases 0).
 */
export const ROTATION_SNAP_GUIDE_DEGREES = [0, 45, 90, 135, 180, 225, 270, 315] as const

export const SNAP_ANGLES: readonly number[] = [...ROTATION_SNAP_GUIDE_DEGREES, 360]

export const ROTATION_SNAP_GUIDE_EXACT_SET = new Set<number>(ROTATION_SNAP_GUIDE_DEGREES)

/** True when `degrees` is exactly on the snap guide grid (no ±threshold). */
export function isExactRotationSnapGuideDegree(degrees: number): boolean {
  if (!Number.isFinite(degrees)) return false
  const normalized = ((Math.round(degrees) % 360) + 360) % 360

  return ROTATION_SNAP_GUIDE_EXACT_SET.has(normalized)
}

/**
 * Canvas rotation snap: Shift → 15° hard snap; otherwise optional 45° magnetic smart guides.
 * Pass `otherAngles` to also snap to the rotation angles of other objects on the canvas.
 */
export function resolveSnappedRotationDegrees(
  rawDegrees: number,
  opts: { shiftKey: boolean; smartGuidesEnabled: boolean; otherAngles?: number[] }
): ResolvedRotationSnap {
  if (opts.shiftKey) {
    return {
      degrees: snapRotationToIncrement(rawDegrees, ROTATION_SHIFT_SNAP_INCREMENT),
      didShiftSnap: true,
      didSmartGuideSnap: false,
      matchedOtherAngle: false,
    }
  }

  if (opts.smartGuidesEnabled) {
    const snap = snapRotationAngle(rawDegrees, 5, opts.otherAngles)

    return {
      degrees: snap.didSnap ? snap.snapped : normalizeRotationDegrees(rawDegrees),
      didShiftSnap: false,
      didSmartGuideSnap: snap.didSnap,
      matchedOtherAngle: snap.matchedOther,
    }
  }

  return {
    degrees: normalizeRotationDegrees(rawDegrees),
    didShiftSnap: false,
    didSmartGuideSnap: false,
    matchedOtherAngle: false,
  }
}

/**
 * Snap a rotation angle to the nearest standard angle if within threshold.
 * Pass `otherAngles` to also snap to the rotation angles of other canvas objects.
 */
export function snapRotationAngle(
  degrees: number,
  threshold = 5,
  otherAngles?: number[]
): { snapped: number; didSnap: boolean; matchedOther: boolean } {
  const normalized = ((degrees % 360) + 360) % 360

  // Try the fixed grid first (0°, 45°, 90°, … 315°, 360°).
  for (const angle of SNAP_ANGLES) {
    if (Math.abs(normalized - angle) <= threshold) {
      const snapped = angle === 360 ? 0 : angle

      return { snapped, didSnap: true, matchedOther: false }
    }
  }

  // Then try other objects' angles.
  if (otherAngles) {
    for (const other of otherAngles) {
      const otherNorm = ((other % 360) + 360) % 360

      if (Math.abs(normalized - otherNorm) <= threshold) {
        return { snapped: otherNorm, didSnap: true, matchedOther: true }
      }
    }
  }

  return { snapped: normalized, didSnap: false, matchedOther: false }
}
